import React from 'react'
import { LogOut, Plus, Settings, Building, Menu, Globe } from 'lucide-react'
import { Button } from '@/components'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@/contexts/LanguageContext'
import { authService } from '@/services/authService'
import type { useTurbineManagementViewModel } from '../useTurbineManagementViewModel'

type VM = ReturnType<typeof useTurbineManagementViewModel>

interface ManagementHeaderProps {
  canAddTurbine: VM['canAddTurbine']
  hasSuperAdminAccess: VM['hasSuperAdminAccess']
  setShowAddModal: VM['setShowAddModal']
  isMobileMenuOpen: VM['isMobileMenuOpen']
  setIsMobileMenuOpen: VM['setIsMobileMenuOpen']
}

const ManagementHeader: React.FC<ManagementHeaderProps> = ({
  canAddTurbine,
  hasSuperAdminAccess,
  setShowAddModal,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const { t, language, setLanguage } = useLanguage()
  const navigate = useNavigate()

  return (
      <header className="bg-white shadow-sm border-b relative z-50">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{t.dashboard.companyName}</h1>
                  <p className="text-xs text-gray-500">{t.dashboard.turbineManagement}</p>
                </div>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-3">
              {canAddTurbine && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t.dashboard.addTurbine}
                </Button>
              )}

              {hasSuperAdminAccess && (
                <Button
                  onClick={() => navigate('/dashboard/companies')}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                >
                  <Building className="w-4 h-4 mr-2" />
                  {t.dashboard.partnerManagement}
                </Button>
              )}
              {hasSuperAdminAccess && (
              <Button
                onClick={() => navigate('/dashboard/users')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t.dashboard.userSettings}
              </Button>
              )}
              
              {/* Language Toggle Button */}
              <Button
                onClick={() => {
                  const newLanguage = language === 'tr' ? 'en' : 'tr'
                  setLanguage(newLanguage)
                }}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors min-w-[80px]"
                title={language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'tr' ? 'English' : 'Türkçe'}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                onClick={async () => {
                  try {
                    await authService.logout()
                    window.location.href = '/'
                  } catch (error) {
                    // Kullaniciya hata GOSTERILMEZ: cikis istegi basarisiz olsa da
                    // yerel oturum temizlenip ana sayfaya gidiliyor, yani kullanici
                    // acisindan islem BASARILI. Hata yalnizca teshis icin loglanir.
                    console.error('Logout error:', error)
                    window.location.href = '/'
                  }
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t.dashboard.logout}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <Button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <Menu className="w-5 h-5" />
              </Button>
          </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-xl z-[99999] w-full">
              <div className="p-4 space-y-2">
                {canAddTurbine && (
                  <Button
                    onClick={() => {
                      setShowAddModal(true)
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white justify-start"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t.dashboard.addTurbine}
                  </Button>
                )}
                {hasSuperAdminAccess && (
                  <Button
                    onClick={() => {
                      navigate('/dashboard/companies')
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white justify-start"
                    size="sm"
                  >
                    <Building className="w-4 h-4 mr-2" />
                    {t.dashboard.partnerManagement}
                  </Button>
                )}
                {hasSuperAdminAccess && (
                  <Button
                    onClick={() => {
                      navigate('/dashboard/users')
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white justify-start"
                    size="sm"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t.dashboard.userSettings}
                  </Button>
                )}
                
                {/* Language Toggle Button for Mobile */}
                <Button
                  onClick={() => {
                    const newLanguage = language === 'tr' ? 'en' : 'tr'
                    setLanguage(newLanguage)
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors justify-start"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {language === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors justify-start"
                  onClick={async () => {
                    try {
                      await authService.logout()
                      window.location.href = '/'
                    } catch (error) {
                      // Kullaniciya hata GOSTERILMEZ: cikis istegi basarisiz olsa da
                      // yerel oturum temizlenip ana sayfaya gidiliyor, yani kullanici
                      // acisindan islem BASARILI. Hata yalnizca teshis icin loglanir.
                      console.error('Logout error:', error)
                      window.location.href = '/'
                    }
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t.dashboard.logout}
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>
  )
}

export default ManagementHeader
